import type { ApiWindow } from "./apiTypes";

const MANILA_OFFSET_MS = 8 * 60 * 60 * 1000;
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export function manilaDateParts(date: Date) {
  const shifted = new Date(date.getTime() + MANILA_OFFSET_MS);
  return {
    month: MONTHS[shifted.getUTCMonth()] ?? "",
    day: shifted.getUTCDate(),
    year: shifted.getUTCFullYear(),
    hours: shifted.getUTCHours(),
    minutes: shifted.getUTCMinutes(),
  };
}

export function formatClock(hours24: number, minutes: number): string {
  const suffix = hours24 >= 12 ? "PM" : "AM";
  const hours12 = hours24 % 12 || 12;
  return `${hours12}:${String(minutes).padStart(2, "0")} ${suffix}`;
}

/** UTC epoch ms of the Manila-local midnight that starts the day containing `date`, shifted by `dayOffset` days. */
export function manilaDayStartMs(date: Date, dayOffset = 0): number {
  const shifted = new Date(date.getTime() + MANILA_OFFSET_MS);
  const midnightShifted = Date.UTC(shifted.getUTCFullYear(), shifted.getUTCMonth(), shifted.getUTCDate() + dayOffset);
  return midnightShifted - MANILA_OFFSET_MS;
}

/** "Today" / "Tomorrow" / "Yesterday" relative to `now` in Manila time, else "Sun, Sep 7". */
export function manilaDayLabel(date: Date, now: Date): string {
  const dayMs = 24 * 60 * 60 * 1000;
  const delta = Math.round((manilaDayStartMs(date) - manilaDayStartMs(now)) / dayMs);
  if (delta === 0) {
    return "Today";
  }
  if (delta === 1) {
    return "Tomorrow";
  }
  if (delta === -1) {
    return "Yesterday";
  }
  const parts = manilaDateParts(date);
  const weekday = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][new Date(date.getTime() + MANILA_OFFSET_MS).getUTCDay()] ?? "";
  return `${weekday}, ${parts.month} ${parts.day}`;
}

/** "2 hr 30 min" / "45 min" / "1 day 2 hr" for the length of a window; empty when it cannot be measured. */
export function formatWindowDuration(window: ApiWindow | null | undefined): string {
  if (!window) {
    return "";
  }
  const ms = new Date(window.end).getTime() - new Date(window.start).getTime();
  if (!Number.isFinite(ms) || ms <= 0) {
    return "";
  }
  const totalMinutes = Math.round(ms / 60000);
  const days = Math.floor(totalMinutes / 1440);
  const hours = Math.floor((totalMinutes % 1440) / 60);
  const minutes = totalMinutes % 60;
  const parts: string[] = [];
  if (days > 0) {
    parts.push(`${days} ${days === 1 ? "day" : "days"}`);
  }
  if (hours > 0) {
    parts.push(`${hours} hr`);
  }
  if (days === 0 && minutes > 0) {
    parts.push(`${minutes} min`);
  }
  return parts.join(" ") || "1 min";
}

/** "10:00 AM – 12:30 PM" (Manila clock times only, no date). */
export function formatManilaClockRange(window: ApiWindow): string {
  const start = manilaDateParts(new Date(window.start));
  const end = manilaDateParts(new Date(window.end));
  return `${formatClock(start.hours, start.minutes)} – ${formatClock(end.hours, end.minutes)}`;
}

export function formatManilaDateTime(date: Date): string {
  const parts = manilaDateParts(date);
  return `${parts.month} ${parts.day}, ${formatClock(parts.hours, parts.minutes)} PHT`;
}

export function formatManilaWindow(window: ApiWindow | null): string {
  if (!window) {
    return "";
  }
  if (window.localLabel) {
    return window.localLabel;
  }

  const start = manilaDateParts(new Date(window.start));
  const end = manilaDateParts(new Date(window.end));
  const sameDate = start.year === end.year && start.month === end.month && start.day === end.day;
  if (sameDate) {
    return `${start.month} ${start.day}, ${formatClock(start.hours, start.minutes)}-${formatClock(end.hours, end.minutes)}`;
  }
  return `${start.month} ${start.day}, ${formatClock(start.hours, start.minutes)}-${end.month} ${end.day}, ${formatClock(
    end.hours,
    end.minutes,
  )}`;
}

function countdownPhrase(now: Date, target: Date, kind: "starts_in" | "ends_in"): { past: true; word: string } | { past: false; text: string } {
  const diffMs = target.getTime() - now.getTime();
  if (diffMs <= 0) {
    return { past: true, word: kind === "starts_in" ? "Started" : "Ended" };
  }

  const totalMinutes = Math.max(1, Math.ceil(diffMs / 60000));
  const days = Math.floor(totalMinutes / 1440);
  const hours = Math.floor((totalMinutes % 1440) / 60);
  const minutes = totalMinutes % 60;
  const parts: string[] = [];

  if (days > 0) {
    parts.push(`${days} ${days === 1 ? "day" : "days"}`);
  }
  if (hours > 0) {
    parts.push(`${hours} hr`);
  }
  if (days === 0 && minutes > 0) {
    parts.push(`${minutes} min`);
  }

  const prefix = kind === "starts_in" ? "Starts" : "Ends";
  return { past: false, text: `${prefix} in ${parts.join(" ")}` };
}

export function formatCountdownShort(now: Date, target: Date, kind: "starts_in" | "ends_in"): string | null {
  const phrase = countdownPhrase(now, target, kind);
  return phrase.past ? null : phrase.text;
}

export function formatCountdown(now: Date, target: Date, kind: "starts_in" | "ends_in"): string {
  const phrase = countdownPhrase(now, target, kind);
  const targetLabel = formatManilaDateTime(target);
  return phrase.past ? `${phrase.word} ${targetLabel}` : `${phrase.text} (${targetLabel})`;
}

export function formatRelativeTime(iso: string | null | undefined, now = new Date()): string {
  if (!iso) {
    return "an unknown time ago";
  }
  const elapsedMs = Math.max(0, now.getTime() - new Date(iso).getTime());
  const minutes = Math.max(1, Math.round(elapsedMs / 60000));
  if (minutes < 60) {
    return `${minutes} minute${minutes === 1 ? "" : "s"} ago`;
  }
  const hours = Math.round(minutes / 60);
  if (hours < 48) {
    return `${hours} hour${hours === 1 ? "" : "s"} ago`;
  }
  const days = Math.round(hours / 24);
  return `${days} day${days === 1 ? "" : "s"} ago`;
}
